import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  FormControl,
  Icon,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { isEmptyFilterValue } from 'utils';

const SELECT_MENU_ITEM_HEIGHT = 48;

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '100%',
    marginBottom: theme.spacing(2),
  },
  // Search is kept separate from filters so the toolbar reads as two clear actions.
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    width: '100%',
    marginBottom: theme.spacing(2),
    '& > *': {
      marginRight: theme.spacing(1),
    },
  },
  // Filters always live below search; the divider reinforces that visual separation.
  separator: {
    width: '100%',
    height: 1,
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.divider,
  },
  filterRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    width: '100%',
    '& > *': {
      marginRight: theme.spacing(2),
      marginBottom: theme.spacing(1),
    },
  },
  search: {
    width: 320,
    flexShrink: 1,

    '@media (max-width: 480px)': {
      width: 'auto',
      flex: 1,
      minWidth: 180,
    },
  },
  searchButtonText: {
    '@media (max-width: 480px)': {
      display: 'none',
    },
  },
  select: {
    width: 280,
    maxWidth: '100%',
    flexShrink: 0,
  },
  // Move the dropdown arrow left when the inline clear button is visible.
  selectWithClear: {
    '& .MuiSelect-icon': {
      right: theme.spacing(4),
    },
  },
  selectClearButton: {
    position: 'absolute',
    right: theme.spacing(0.5),
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,
  },
  selectRenderValue: {
    display: 'block',
    maxWidth: 210,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const getOptionLabel = (options, value) => {
  const option = options.find(item => item.value === value || String(item.value) === String(value));
  return option ? option.label : value;
};

const getFilterValue = (filter, filtering) => {
  const isMultiple = filter.type === 'multiSelect';
  const value = filtering[filter.name];

  if (isEmptyFilterValue(value)) {
    return isMultiple ? [] : '';
  }

  return isMultiple && !Array.isArray(value) ? [value] : value;
};

const TableFilterToolbar = ({ filters, filtering, onFilterChange, onRemoveFilter }) => {
  const classes = useStyles();
  const { t } = useTranslation('common');

  const searchFilter = filters.find(filter => filter.type === 'search');
  const selectFilters = filters.filter(filter => filter.type !== 'search');
  const searchFilterName = searchFilter && searchFilter.name;

  const [searchValue, setSearchValue] = useState(searchFilter ? filtering[searchFilter.name] || '' : '');

  // Keep local input state in sync when filters are changed externally.
  useEffect(() => {
    if (searchFilterName) {
      setSearchValue(filtering[searchFilterName] || '');
    }
  }, [filtering, searchFilterName]);

  // Search is applied explicitly by pressing Enter or clicking the search button.
  const handleSearch = () => {
    if (!searchFilter) return;

    const normalizedValue = searchValue.trim();

    if (normalizedValue) {
      onFilterChange(searchFilter.name, normalizedValue);
    } else {
      onRemoveFilter(searchFilter.name);
    }
  };

  return (
    <div className={classes.root}>
      {searchFilter && (
        <div className={classes.searchRow}>
          <TextField
            className={classes.search}
            label={searchFilter.label}
            placeholder={searchFilter.placeholder}
            value={searchValue}
            onChange={event => setSearchValue(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                handleSearch();
              }
            }}
            InputProps={{
              endAdornment: searchValue ? (
                <IconButton
                  size="small"
                  aria-label={t('tableFilters.clearSearch')}
                  onClick={() => {
                    setSearchValue('');
                    onRemoveFilter(searchFilter.name);
                  }}
                >
                  <Icon fontSize="small">clear</Icon>
                </IconButton>
              ) : null,
            }}
            variant="outlined"
            size="small"
          />

          <Button color="primary" variant="contained" startIcon={<Icon>search</Icon>} onClick={handleSearch}>
            <span className={classes.searchButtonText}>{t('tableFilters.applySearch')}</span>
          </Button>
        </div>
      )}

      {searchFilter && selectFilters.length > 0 && <div className={classes.separator} />}

      {selectFilters.length > 0 && (
        <div className={classes.filterRow}>
          {selectFilters.map(filter => {
            const isMultiple = filter.type === 'multiSelect';
            const value = getFilterValue(filter, filtering);
            const selectedValues = Array.isArray(value) ? value : [value];
            const hasValue = !isEmptyFilterValue(value);

            return (
              <FormControl key={filter.name} className={classes.select} variant="outlined" size="small">
                <InputLabel id={`${filter.name}-label`}>{filter.label}</InputLabel>

                <Select
                  className={hasValue ? classes.selectWithClear : undefined}
                  labelId={`${filter.name}-label`}
                  multiple={isMultiple}
                  value={value}
                  label={filter.label}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: SELECT_MENU_ITEM_HEIGHT * (filter.menuMaxVisibleItems || 10),
                      },
                    },
                  }}
                  onChange={event => {
                    const nextValue = event.target.value;

                    if (isEmptyFilterValue(nextValue)) {
                      onRemoveFilter(filter.name);
                    } else {
                      onFilterChange(filter.name, nextValue);
                    }
                  }}
                  renderValue={selected => {
                    const values = Array.isArray(selected) ? selected : [selected];

                    return (
                      <span className={classes.selectRenderValue}>
                        {values.map(item => getOptionLabel(filter.options || [], item)).join(', ')}
                      </span>
                    );
                  }}
                >
                  {!isMultiple && (
                    <MenuItem value="">
                      <em>{t('tableFilters.all')}</em>
                    </MenuItem>
                  )}

                  {(filter.options || []).map(option => (
                    <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                      {isMultiple && (
                        <Checkbox
                          checked={selectedValues.includes(option.value)}
                          color="primary"
                          disabled={option.disabled}
                        />
                      )}
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))}
                </Select>

                {hasValue && (
                  <IconButton
                    size="small"
                    className={classes.selectClearButton}
                    aria-label={t('tableFilters.clearFilter')}
                    onClick={event => {
                      event.stopPropagation();
                      onRemoveFilter(filter.name);
                    }}
                  >
                    <Icon fontSize="small">clear</Icon>
                  </IconButton>
                )}
              </FormControl>
            );
          })}
        </div>
      )}
    </div>
  );
};

TableFilterToolbar.defaultProps = {
  filtering: {},
};

TableFilterToolbar.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['search', 'singleSelect', 'multiSelect']).isRequired,
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      placeholder: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
          label: PropTypes.string.isRequired,
          disabled: PropTypes.bool,
        }),
      ),
      menuMaxVisibleItems: PropTypes.number,
    }),
  ).isRequired,
  filtering: PropTypes.shape({}),
  onFilterChange: PropTypes.func.isRequired,
  onRemoveFilter: PropTypes.func.isRequired,
};

export default TableFilterToolbar;
